import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Paper,
  IconButton,
  Pagination,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  CommonList,
  ConfirmSelectionButton,
  ListSelectionMode,
  SelectionPageHeader,
} from '../../common/components';
import { ContactFilters } from '../components/ContactFilters';
import type { Contact } from '../../../types/contact';
import type { ContactSelectionContext } from '../types';
import { formatContactType, getTypeFilterForMode } from '../types';
import { formatContactFullName } from '../../../utils/contactName';
import { useContactData } from '../useContactData';
import { useContactSingleSelection } from './useContactSingleSelection';

export default function ContactSingleSelectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = id ? parseInt(id) : null;

  const selectionContext = location.state as ContactSelectionContext;
  const typeFilter = getTypeFilterForMode(selectionContext?.mode);

  const { data, filters, pagination } = useContactData({ companyId, typeFilter });
  const { selectedContact, handleSelectContact } = useContactSingleSelection();

  const handleConfirmSelection = () => {
    if (selectedContact && selectionContext?.returnPath) {
      const isProvider = typeFilter === 'PROVIDER';
      navigate(selectionContext.returnPath, {
        state: {
          [isProvider ? 'selectedProvider' : 'selectedClient']: selectedContact,
          activityFeId: selectionContext?.activityFeId,
        },
      });
    }
  };

  const handleDoubleClick = (contact: Contact) => {
    if (selectionContext?.returnPath) {
      const isProvider = typeFilter === 'PROVIDER';
      navigate(selectionContext.returnPath, {
        state: {
          [isProvider ? 'selectedProvider' : 'selectedClient']: contact,
          activityFeId: selectionContext?.activityFeId,
        },
      });
    }
  };

  if (data.isFirstLoad) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          {data.error}
        </Typography>
      </Box>
    );
  }

  const label = typeFilter === 'PROVIDER' ? 'provider' : 'client';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <SelectionPageHeader
        title={`Вибір ${label === 'client' ? 'клієнта' : 'постачальника'}`}
        companyId={companyId}
        returnPath={selectionContext?.returnPath}
      >
        <Tooltip title="Показати/сховати фільтри">
          <IconButton
            size="small"
            onClick={() => filters.setShowFilters(!filters.showFilters)}
            color={filters.showFilters || filters.hasActiveFilters ? 'primary' : 'default'}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>

        {selectedContact && <ConfirmSelectionButton onClick={handleConfirmSelection} />}
      </SelectionPageHeader>

      <ContactFilters
        show={filters.showFilters}
        filterName={filters.filterName}
        onFilterNameChange={filters.setFilterName}
        filterPhone={filters.filterPhone}
        onFilterPhoneChange={filters.setFilterPhone}
        onClearFilters={filters.handleClearFilters}
      />

      {data.loading && <LinearProgress sx={{ mb: 2 }} />}

      {data.contacts.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 6 },
            textAlign: 'center',
            backgroundColor: 'background.default',
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" gutterBottom>
            Немає {label === 'client' ? 'клієнтів' : 'постачальників'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Немає {label === 'client' ? 'клієнтів' : 'постачальників'} для вибору.
          </Typography>
        </Paper>
      ) : (
        <>
          <CommonList<Contact>
            items={data.contacts}
            toListItem={(item) => ({
              id: item.id,
              primary: formatContactFullName(item),
              secondary: [item.phoneNumber, item.organizationId ? data.organizationNamesById.get(item.organizationId) : undefined]
                .filter(Boolean)
                .join(' · '),
              tertiary: formatContactType(item.type),
            })}
            selectionMode={ListSelectionMode.SINGLE_SELECTION}
            selectedItemId={selectedContact?.id || selectionContext?.currentContactId}
            onSelect={handleSelectContact}
            onDoubleClick={handleDoubleClick}
            emptyMessage="Контакти не знайдено"
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              {...pagination}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}
    </Box>
  );
}
